import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  isTransferCatalogAbortError,
  postTransferProductCatalog,
} from "../../../Components/InventoryTransfer/transferCatalogApi";

export const transferCatalogInitialState = {
  items: [],
  loading: false,
  loadingMore: false,
  error: null,
  /** Last successful response pagination from API */
  pagination: null,
  /** Next row skip for infinite scroll (pageIndex * limit, e.g. 0, 20, 40) */
  nextOffset: 0,
  hasMore: false,
  /** Latest non-append request id — ignore stale fulfill/reject after abort/replace. */
  activeRequestId: null,
  /** True after the latest non-append fetch settles (success or real error). */
  hasFetched: false,
};

const initialState = transferCatalogInitialState;

/** Safe selector — avoids crash if reducer is not mounted on the store yet. */
export function selectTransferCatalog(state) {
  return state?.transferCatalog ?? transferCatalogInitialState;
}

/**
 * Load products for New Transfer catalog via POST /products.
 * Set `append: true` to merge the next page (infinite scroll).
 */
export const fetchTransferCatalog = createAsyncThunk(
  "transferCatalog/fetch",
  async (
    {
      token,
      merchantId,
      tokenId,
      loginType,
      sessionId,
      loginEmployeeId,
      limit = 20,
      offset = 0,
      filter_sort_by = 1,
      filter_order_by = "DESC",
      append = false,
      search = "",
      signal,
    },
    { rejectWithValue },
  ) => {
    if (!token) {
      return rejectWithValue("Not authenticated");
    }
    if (merchantId == null || merchantId === "") {
      return rejectWithValue("Missing merchant");
    }
    try {
      const { items, pagination } = await postTransferProductCatalog({
        token,
        merchant_id: merchantId,
        token_id: tokenId,
        login_type: loginType ?? "merchant",
        session_id: sessionId,
        login_employee_id: loginEmployeeId ?? null,
        search,
        limit,
        offset,
        filter_sort_by,
        filter_order_by,
        signal,
      });
      const list = Array.isArray(items) ? items : [];
      return {
        items: list,
        pagination,
        requestedOffset: offset,
        limit,
        append,
      };
    } catch (err) {
      if (isTransferCatalogAbortError(err)) {
        return rejectWithValue({ aborted: true });
      }
      const msg =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err?.message ||
        "Could not load products.";
      return rejectWithValue(typeof msg === "string" ? msg : JSON.stringify(msg));
    }
  },
  {
    condition: (arg, { getState }) => {
      if (!arg.append) return true;
      const catalog = getState().transferCatalog ?? transferCatalogInitialState;
      if (catalog.loading || catalog.loadingMore) return false;
      return true;
    },
  },
);

const transferCatalogSlice = createSlice({
  name: "transferCatalog",
  initialState,
  reducers: {
    clearTransferCatalog: () => initialState,
    clearTransferCatalogError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchTransferCatalog.pending, (state, action) => {
        const append = action.meta.arg?.append;
        if (append) {
          state.loadingMore = true;
        } else {
          state.activeRequestId = action.meta.requestId;
          state.loading = true;
          state.hasFetched = false;
          state.items = [];
          state.pagination = null;
          state.nextOffset = 0;
          state.hasMore = false;
        }
        state.error = null;
      })
      .addCase(fetchTransferCatalog.fulfilled, (state, action) => {
        const append = action.meta.arg?.append;
        if (append) {
          state.loadingMore = false;
        } else if (state.activeRequestId !== action.meta.requestId) {
          // Stale response after a newer catalog fetch started.
          return;
        } else {
          state.loading = false;
          state.hasFetched = true;
          state.activeRequestId = null;
        }
        state.error = null;

        const {
          items: newItems,
          pagination,
          requestedOffset,
          limit: pageLimit,
        } = action.payload;
        const lim = pageLimit || pagination?.limit || 20;
        const total = pagination?.total ?? 0;

        if (append) {
          const existingIds = new Set(state.items.map((p) => p.id));
          const merged = newItems.filter((p) => !existingIds.has(p.id));
          state.items = [...state.items, ...merged];
        } else {
          state.items = newItems;
        }

        state.pagination = pagination;
        // API expects row skip: offset = pageIndex * limit (0, 20, 40, …).
        const currentOffset = Number(requestedOffset ?? 0);
        state.nextOffset = currentOffset + newItems.length;

        if (append && newItems.length === 0) {
          state.hasMore = false;
        } else if (total > 0) {
          state.hasMore = state.items.length < total;
        } else {
          state.hasMore = newItems.length >= lim;
        }
      })
      .addCase(fetchTransferCatalog.rejected, (state, action) => {
        const append = action.meta.arg?.append;
        const aborted =
          action.meta?.aborted || action.payload?.aborted === true;

        if (aborted) {
          if (append) {
            state.loadingMore = false;
          }
          // Ignore aborted non-append: a replacement fetch owns loading state.
          return;
        }

        if (append) {
          state.loadingMore = false;
          return;
        }

        if (state.activeRequestId !== action.meta.requestId) {
          return;
        }

        state.loading = false;
        state.hasFetched = true;
        state.activeRequestId = null;
        state.items = [];
        state.pagination = null;
        state.nextOffset = 0;
        state.hasMore = false;
        const payload = action.payload;
        state.error =
          typeof payload === "string"
            ? payload
            : action.error?.message || "Request failed";
      });
  },
});

export const { clearTransferCatalog, clearTransferCatalogError } =
  transferCatalogSlice.actions;

export default transferCatalogSlice.reducer;
